import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  Database,
  Download,
  Edit3,
  FileSpreadsheet,
  Globe,
  HardDrive,
  LayoutDashboard,
  Menu,
  Plus,
  RefreshCw,
  Search,
  Upload,
  X
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const BRAND = {
  primary: 'bg-[#74c476]',
  primaryHover: 'hover:bg-[#5aa85c]',
  secondary: 'bg-[#222222]',
  accent: 'text-[#74c476]',
  light: 'bg-stone-50'
};

const CHART_COLORS = ['#74c476', '#5aa85c', '#4E647D', '#E4B063', '#D76559', '#6b7280'];

const formatIsoDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return raw;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }).format(new Date(parsed));
};

const toDateInputValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const isoDatePart = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s].*$/);
  if (isoDatePart) return isoDatePart[1];

  const dmy = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  if (dmy) {
    const day = String(parseInt(dmy[1], 10)).padStart(2, '0');
    const month = String(parseInt(dmy[2], 10)).padStart(2, '0');
    const yearRaw = String(dmy[3]);
    const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
    return `${year}-${month}-${day}`;
  }

  return '';
};

const authFetchLocal = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{label}</div>
    <div className="text-2xl font-bold text-stone-900">{value}</div>
    {hint ? <div className="text-sm text-stone-500 mt-1">{hint}</div> : null}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
    <h3 className="text-sm font-bold mb-3">{title}</h3>
    <div className="h-[300px]">{children}</div>
  </div>
);

const FormField = ({ label, value, onChange, multiline = false, type = 'text', disabled = false }) => (
  <label className="block">
    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">{label}</span>
    {multiline ? (
      <textarea disabled={disabled} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border border-stone-300 p-2 text-sm min-h-[84px] disabled:bg-stone-100 disabled:text-stone-500" />
    ) : (
      <input disabled={disabled} type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border border-stone-300 p-2 text-sm disabled:bg-stone-100 disabled:text-stone-500" />
    )}
  </label>
);

const EMPTY_COMMISSION_FORM = {
  id: '',
  idnous: '',
  name: '',
  matchingName: '',
  matchingId: '',
  qDemCorretgit: '',
  pepeMal: '',
  website: '',
  capCode: '',
  capSubcode: '',
  capTopic: '',
  capSubtopic: '',
  createdYear: '',
  legislature: '',
  createdDate: '',
  latestNormDate: '',
  latestNormText: '',
  composition: '',
  functions: '',
  legalFormText: '',
  legalFormCategory: '',
  dependency: '',
  spiType: '',
  economicEndowmentType: '',
  economicEndowmentText: '',
  membersPaidType: '',
  membersPaidText: '',
  repealed: false
};

const CodeSelectField = ({ label, value, options, onChange }) => (
  <label className="block">
    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">{label}</span>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
      <option value="">Not specified</option>
      {(options || []).map((opt) => (
        <option key={`${label}-${opt.code}`} value={opt.code}>
          {opt.code}{opt.label ? ` - ${opt.label}` : ''}
        </option>
      ))}
    </select>
  </label>
);

const SimpleSelectField = ({ label, value, options, onChange }) => (
  <label className="block">
    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">{label}</span>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
      <option value="">Not specified</option>
      {(options || []).map((opt) => (
        <option key={`${label}-${opt}`} value={opt}>{opt}</option>
      ))}
    </select>
  </label>
);

export default function SpanishCommissionsWorkspace({ user, onLogout, onBackToHub, apiUrl }) {
  const [view, setView] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ topics: [], dependencies: [], legalForms: [] });
  const [search, setSearch] = useState('');
  const [topic, setTopic] = useState('');
  const [dependency, setDependency] = useState('');
  const [legalForm, setLegalForm] = useState('');
  const [status, setStatus] = useState('');
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [detailBackView, setDetailBackView] = useState('commissions');
  const [importMsg, setImportMsg] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editTab, setEditTab] = useState('commission');
  const [commissionForm, setCommissionForm] = useState(null);
  const [membersForm, setMembersForm] = useState([]);
  const [saveMsg, setSaveMsg] = useState(null);
  const [metadata, setMetadata] = useState({
    capTopics: [],
    capSubtopics: [],
    actorTypes: [],
    interestGroupTypes: [],
    legislatures: [],
    dependencies: [],
    legalForms: [],
    spiTypes: [],
    economicEndowmentTypes: [],
    membersPaidTypes: []
  });

  const displayUserName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.fullName || user?.username || 'User';
  const canEdit = user && ['admin', 'coder'].includes(user.role);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-commissions/dashboard`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load commissions dashboard');
      setDashboard(data);
    } catch (error) {
      console.error(error);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchMetadata = useCallback(async () => {
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-commissions/metadata`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load metadata');
      setMetadata({
        capTopics: Array.isArray(data?.capTopics) ? data.capTopics : [],
        capSubtopics: Array.isArray(data?.capSubtopics) ? data.capSubtopics : [],
        actorTypes: Array.isArray(data?.actorTypes) ? data.actorTypes : [],
        interestGroupTypes: Array.isArray(data?.interestGroupTypes) ? data.interestGroupTypes : [],
        legislatures: Array.isArray(data?.legislatures) ? data.legislatures : [],
        dependencies: Array.isArray(data?.dependencies) ? data.dependencies : [],
        legalForms: Array.isArray(data?.legalForms) ? data.legalForms : [],
        spiTypes: Array.isArray(data?.spiTypes) ? data.spiTypes : [],
        economicEndowmentTypes: Array.isArray(data?.economicEndowmentTypes) ? data.economicEndowmentTypes : [],
        membersPaidTypes: Array.isArray(data?.membersPaidTypes) ? data.membersPaidTypes : []
      });
    } catch (error) {
      console.error(error);
      setMetadata({
        capTopics: [],
        capSubtopics: [],
        actorTypes: [],
        interestGroupTypes: [],
        legislatures: [],
        dependencies: [],
        legalForms: [],
        spiTypes: [],
        economicEndowmentTypes: [],
        membersPaidTypes: []
      });
    }
  }, [apiUrl]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (topic) params.set('topic', topic);
      if (dependency) params.set('dependency', dependency);
      if (legalForm) params.set('legalForm', legalForm);
      if (status) params.set('status', status);
      const query = params.toString();
      const res = await authFetchLocal(`${apiUrl}/spanish-commissions/commissions${query ? `?${query}` : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load commissions');
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setFacets({
        topics: Array.isArray(data?.facets?.topics) ? data.facets.topics : [],
        dependencies: Array.isArray(data?.facets?.dependencies) ? data.facets.dependencies : [],
        legalForms: Array.isArray(data?.facets?.legalForms) ? data.facets.legalForms : []
      });
    } catch (error) {
      console.error(error);
      setRows([]);
      setFacets({ topics: [], dependencies: [], legalForms: [] });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, dependency, legalForm, search, status, topic]);

  const fetchCommission = useCallback(async (commissionId, backView = 'commissions') => {
    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-commissions/commissions/${encodeURIComponent(commissionId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load commission');
      setSelectedCommission(data?.row || null);
      setDetailBackView(backView);
      setIsEditMode(false);
      setIsCreateMode(false);
      setSaveMsg(null);
      setCommissionForm(null);
      setMembersForm([]);
      setView('detail');
      setMobileMenuOpen(false);
    } catch (error) {
      console.error(error);
      setSelectedCommission(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDashboard();
    fetchMetadata();
  }, [fetchDashboard, fetchMetadata]);

  useEffect(() => {
    if (view === 'commissions') fetchRows();
  }, [fetchRows, view]);

  const topTopicChart = useMemo(() => (dashboard?.byTopic || []).slice(0, 8).map((item) => ({ name: item.label, count: item.count })), [dashboard]);
  const dependencyChart = useMemo(() => (dashboard?.byDependency || []).slice(0, 8).map((item) => ({ name: item.label, count: item.count })), [dashboard]);
  const legalFormChart = useMemo(() => (dashboard?.byLegalForm || []).slice(0, 6).map((item) => ({ name: item.label, count: item.count })), [dashboard]);
  const dashboardInsights = useMemo(() => {
    const totalCommissions = Number(dashboard?.summary?.totalCommissions || 0);
    const totalMembers = Number(dashboard?.summary?.totalMembers || 0);
    const withWebsite = Number(dashboard?.summary?.commissionsWithWebsite || 0);
    const repealedCount = Number(dashboard?.summary?.repealedCount || 0);
    const activeCount = Math.max(0, totalCommissions - repealedCount);
    const avgMembers = totalCommissions ? (totalMembers / totalCommissions) : 0;
    const websiteRate = totalCommissions ? ((withWebsite / totalCommissions) * 100) : 0;
    const repealedRate = totalCommissions ? ((repealedCount / totalCommissions) * 100) : 0;
    const topTopicCount = Number(dashboard?.byTopic?.[0]?.count || 0);
    const topTopicLabel = String(dashboard?.byTopic?.[0]?.label || 'Not specified');
    const topTopicRate = totalCommissions ? ((topTopicCount / totalCommissions) * 100) : 0;
    return {
      activeCount,
      avgMembers,
      websiteRate,
      repealedRate,
      topTopicLabel,
      topTopicRate
    };
  }, [dashboard]);

  const startEdit = () => {
    if (!selectedCommission) return;
    const { members, memberCount, namedMemberCount, unnamedMemberCount, ...commissionOnly } = selectedCommission;
    setCommissionForm({
      ...commissionOnly,
      createdDate: toDateInputValue(commissionOnly.createdDate),
      latestNormDate: toDateInputValue(commissionOnly.latestNormDate),
      repealed: !!commissionOnly.repealed
    });
    setMembersForm(Array.isArray(members) ? members.map((member) => ({
      ...member,
      createdDate: toDateInputValue(member.createdDate)
    })) : []);
    setIsCreateMode(false);
    setEditTab('commission');
    setIsEditMode(true);
    setSaveMsg(null);
  };

  const startCreate = () => {
    setDetailBackView(view || 'commissions');
    setSelectedCommission({ id: '', name: 'New Commission', members: [], memberCount: 0, repealed: false });
    setCommissionForm({ ...EMPTY_COMMISSION_FORM });
    setMembersForm([]);
    setIsCreateMode(true);
    setEditTab('commission');
    setIsEditMode(true);
    setSaveMsg(null);
    setView('detail');
    setMobileMenuOpen(false);
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    if (isCreateMode) {
      setIsCreateMode(false);
      setSelectedCommission(null);
      setCommissionForm(null);
      setMembersForm([]);
      setView(detailBackView || 'commissions');
    }
    setSaveMsg(null);
  };

  const saveEdit = async () => {
    if (!selectedCommission) return;
    setLoading(true);
    setSaveMsg(null);
    try {
      const commissionPayload = commissionForm || {};
      const membersPayload = Array.isArray(membersForm) ? membersForm : [];

      if (isCreateMode && !String(commissionPayload.id || '').trim()) {
        throw new Error('ID_qdem is required to create a commission');
      }

      const commissionIdForSave = isCreateMode
        ? String(commissionPayload.id || '').trim()
        : String(selectedCommission.id || '').trim();

      const commissionRes = await authFetchLocal(
        isCreateMode
          ? `${apiUrl}/spanish-commissions/commissions`
          : `${apiUrl}/spanish-commissions/commissions/${encodeURIComponent(commissionIdForSave)}`,
        {
          method: isCreateMode ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(commissionPayload)
        }
      );
      const commissionData = await commissionRes.json();
      if (!commissionRes.ok) throw new Error(commissionData?.error || (isCreateMode ? 'Failed to create commission' : 'Failed to update commission'));

      const replaceMembersRes = await authFetchLocal(`${apiUrl}/spanish-commissions/commissions/${encodeURIComponent(commissionIdForSave)}/members/replace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ members: membersPayload })
      });
      const replaceMembersData = await replaceMembersRes.json();
      if (!replaceMembersRes.ok) throw new Error(replaceMembersData?.error || 'Failed to update members');

      setSelectedCommission(replaceMembersData?.row || commissionData?.row || selectedCommission);
      setIsCreateMode(false);
      setIsEditMode(false);
      setSaveMsg({ type: 'success', text: isCreateMode ? 'Commission created.' : 'Commission and members updated.' });
      await fetchRows();
      await fetchDashboard();
    } catch (error) {
      setSaveMsg({ type: 'error', text: error.message || 'Failed to save changes' });
    } finally {
      setLoading(false);
    }
  };

  const updateCommissionField = (field, value) => {
    setCommissionForm((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const updateMemberField = (index, field, value) => {
    setMembersForm((prev) => prev.map((member, idx) => (idx === index ? { ...member, [field]: value } : member)));
  };

  const addMemberRow = () => {
    setMembersForm((prev) => [...prev, {
      id: `new-${Date.now()}`,
      commissionId: commissionForm?.id || selectedCommission?.id || '',
      commissionName: commissionForm?.name || selectedCommission?.name || ''
    }]);
  };

  const removeMemberRow = (index) => {
    setMembersForm((prev) => prev.filter((_, idx) => idx !== index));
  };

  const downloadBlobFromEndpoint = async (endpoint, filename) => {
    const res = await authFetchLocal(`${apiUrl}${endpoint}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || 'Export failed');
    }
    const contentDisposition = res.headers.get('content-disposition') || '';
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    let resolvedFilename = filename;
    if (filenameMatch?.[1]) {
      try {
        resolvedFilename = decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
      } catch {
        resolvedFilename = filenameMatch[1].replace(/"/g, '').trim() || filename;
      }
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = resolvedFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportCommissionsCsv = async () => {
    try {
      await downloadBlobFromEndpoint(
        '/spanish-commissions/export-csv',
        `spanish_commissions_${new Date().toISOString().split('T')[0]}.csv`
      );
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportCommissionsXlsx = async () => {
    try {
      await downloadBlobFromEndpoint(
        '/spanish-commissions/export-xlsx',
        `spanish_commissions_${new Date().toISOString().split('T')[0]}.xlsx`
      );
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportCommissionsOriginal = async () => {
    try {
      await downloadBlobFromEndpoint(
        '/spanish-commissions/export-original',
        `spanish_commissions_original_${new Date().toISOString().split('T')[0]}.sql`
      );
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
      <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
        <div className="flex flex-col">
          <h1 className="text-lg font-bold font-sans tracking-tight">Spanish Parliament<span className={BRAND.accent}> Commissions</span></h1>
        </div>
        <button onClick={() => setMobileMenuOpen((prev) => !prev)} className="text-white p-1">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileMenuOpen(false)} />}

      <aside className={`fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="p-6 border-b border-stone-700 hidden md:block">
          <h1 className="text-xl font-sans font-bold tracking-tight">Spanish Parliament<span className={BRAND.accent}> Commissions</span></h1>
          <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Q-DEM dataset</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <div className="px-4 py-2 mb-4">
            <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
            <div className="font-medium text-white truncate">{displayUserName}</div>
            <div className="flex justify-between items-center text-xs mt-1">
              <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user?.role}</span>
              <button onClick={onLogout} title="Log Out">
                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          <button onClick={() => { setView('dashboard'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'dashboard' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </button>

          <button onClick={() => { setView('commissions'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'commissions' || view === 'detail' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
            <Database size={18} />
            <span>Commissions</span>
          </button>

          <div className="pt-4 mt-4 border-t border-stone-700 space-y-1">
            {user?.role !== 'viewer' && <button onClick={() => { setView('data'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'data' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
              <HardDrive size={18} />
              <span>Data Management</span>
            </button>}

            <button onClick={() => { onBackToHub(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium text-stone-300 hover:bg-stone-700">
              <Database size={18} />
              <span>Databases Hub</span>
            </button>
          </div>
        </nav>
      </aside>

      <main className={`flex-1 overflow-auto ${BRAND.light} relative p-6 md:p-8`}>
        {view === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between border-b border-stone-300 pb-4 gap-3">
              <div>
                <h2 className="text-3xl font-bold text-stone-900">Dashboard</h2>
                <p className="text-sm text-stone-500 mt-1">Initial structure merged from llistat, taula_mare, taula_detall and variablesnoves.</p>
              </div>
              <button onClick={fetchDashboard} className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50">
                <RefreshCw size={14} /> Refresh stats
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <StatCard label="Commissions" value={dashboard?.summary?.totalCommissions || 0} />
              <StatCard label="Active" value={dashboardInsights.activeCount} />
              <StatCard label="Repealed" value={dashboard?.summary?.repealedCount || 0} hint={`${dashboardInsights.repealedRate.toFixed(1)}%`} />
              <StatCard label="Member Rows" value={dashboard?.summary?.totalMembers || 0} />
              <StatCard label="With Members" value={dashboard?.summary?.commissionsWithMembers || 0} hint="Comisiones con miembros registrados" />
              <StatCard label="Avg Members" value={dashboardInsights.avgMembers.toFixed(1)} />
              <StatCard label="Data Range" value={`${dashboard?.summary?.minYear || '—'} → ${dashboard?.summary?.maxYear || '—'}`} />
              <StatCard label="With Website" value={dashboard?.summary?.commissionsWithWebsite || 0} hint={`${dashboardInsights.websiteRate.toFixed(1)}%`} />
              <StatCard label="Topics" value={dashboard?.summary?.distinctTopics || 0} />
              <StatCard label="Dependencies" value={dashboard?.summary?.distinctDependencies || 0} />
              <StatCard label="Top Topic" value={dashboardInsights.topTopicLabel} hint={`${dashboardInsights.topTopicRate.toFixed(1)}%`} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <ChartCard title="Commissions by Topic">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topTopicChart} margin={{ top: 10, right: 20, left: 0, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={90} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#74c476" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Commissions by Dependency">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dependencyChart} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={60} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4E647D" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Legal Form Category">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={legalFormChart} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius="72%" innerRadius="42%">
                      {legalFormChart.map((entry, index) => <Cell key={`legal-form-${entry.name}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>
        )}

        {view === 'commissions' && (
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
              <h2 className="text-3xl font-bold text-stone-900">Commissions</h2>
              <div className="flex items-center gap-3">
                <div className="text-sm text-stone-500">{rows.length} visible records</div>
                {canEdit ? <button onClick={startCreate} className="px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50 inline-flex items-center gap-2"><Plus size={14} /> Add commission</button> : null}
              </div>
            </div>

            <div className="bg-white border border-stone-200 p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
              <div className="relative xl:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search commission, topic, legal form..." className="w-full border border-stone-300 p-2 pl-9 text-sm" />
              </div>
              <select value={topic} onChange={(e) => setTopic(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All topics</option>{facets.topics.map((value) => <option key={`topic-${value}`} value={value}>{value}</option>)}</select>
              <select value={dependency} onChange={(e) => setDependency(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All dependencies</option>{facets.dependencies.map((value) => <option key={`dep-${value}`} value={value}>{value}</option>)}</select>
              <select value={legalForm} onChange={(e) => setLegalForm(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All legal forms</option>{facets.legalForms.map((value) => <option key={`legal-${value}`} value={value}>{value}</option>)}</select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All statuses</option><option value="active">Active</option><option value="repealed">Repealed</option></select>
              <button onClick={fetchRows} className="border border-stone-300 px-3 py-2 bg-stone-50 text-sm hover:bg-stone-100">Apply filters</button>
            </div>

            <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead className="bg-stone-100 text-stone-600 text-xs font-bold uppercase tracking-wider">
                  <tr>
                    <th className="p-4 border-b border-stone-200">Commission</th>
                    <th className="p-4 border-b border-stone-200">Topic</th>
                    <th className="p-4 border-b border-stone-200">Dependency</th>
                    <th className="p-4 border-b border-stone-200">Legal Form</th>
                    <th className="p-4 border-b border-stone-200">Members</th>
                    <th className="p-4 border-b border-stone-200">Latest Norm</th>
                    <th className="p-4 border-b border-stone-200">Status</th>
                    <th className="p-4 border-b border-stone-200 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-stone-50">
                      <td className="p-4 min-w-[320px]">
                        <div className="font-medium text-stone-900">{row.name}</div>
                        {row.matchingName ? <div className="text-xs text-stone-500 mt-1">Matching: {row.matchingName}</div> : null}
                      </td>
                      <td className="p-4 text-stone-600">{row.capTopic || '—'}</td>
                      <td className="p-4 text-stone-600">{row.dependency || '—'}</td>
                      <td className="p-4 text-stone-600">{row.legalFormCategory || '—'}</td>
                      <td className="p-4 text-stone-600">{row.memberCount}</td>
                      <td className="p-4 text-stone-600 whitespace-nowrap">{formatIsoDate(row.latestNormDate)}</td>
                      <td className="p-4">{row.repealed ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide bg-red-100 text-red-700">Repealed</span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide bg-green-100 text-green-700">Active</span>}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => fetchCommission(row.id, view)} className="font-medium text-green-700 hover:text-green-900 text-xs uppercase tracking-wider">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'detail' && selectedCommission && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 border-b border-stone-300 pb-6">
              <div className="flex items-center gap-4">
                <button onClick={() => setView(detailBackView || 'commissions')} className="p-2 hover:bg-stone-200 rounded-full transition-colors">
                  <ArrowLeft size={20} className="text-stone-600" />
                </button>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-stone-900">{isCreateMode ? 'New Commission' : selectedCommission.name}</h2>
                    {selectedCommission.repealed ? <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded text-xs font-bold border border-red-200 uppercase tracking-wider">Repealed</span> : <div className="text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs font-bold border border-green-200 flex items-center gap-1 uppercase tracking-wider"><CheckCircle size={14} /> Active</div>}
                  </div>
                  <p className="text-stone-500 text-sm mt-1">ID_qdem {selectedCommission.id || 'pending'} · {selectedCommission.capTopic || 'No topic'} · {selectedCommission.memberCount || 0} member rows</p>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  {isEditMode ? (
                    <>
                      <button onClick={cancelEdit} className="px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50">Cancel</button>
                      <button onClick={saveEdit} className="px-3 py-2 bg-[#74c476] text-white text-sm hover:bg-[#5aa85c]">Save All Changes</button>
                    </>
                  ) : (
                    <button onClick={startEdit} className="px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50 inline-flex items-center gap-2"><Edit3 size={14} /> Edit all fields</button>
                  )}
                </div>
              )}
            </div>

            {saveMsg && (
              <div className={`p-3 rounded-sm text-sm ${saveMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                {saveMsg.text}
              </div>
            )}

            {isEditMode && (
              <div className="space-y-4">
                <div className="bg-white rounded-sm shadow-sm border border-stone-200 p-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setEditTab('commission')}
                    className={`px-3 py-2 text-sm border ${editTab === 'commission' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                  >
                    Commission
                  </button>
                  <button
                    onClick={() => setEditTab('members')}
                    className={`px-3 py-2 text-sm border ${editTab === 'members' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                  >
                    Members
                  </button>
                </div>

                {editTab === 'commission' && (
                <div className="bg-white rounded-sm shadow-sm border border-stone-200 p-6 space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-stone-500">Commission fields</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormField label="ID_qdem" value={commissionForm?.id} onChange={(value) => updateCommissionField('id', value)} disabled={!isCreateMode} />
                    <FormField label="Name" value={commissionForm?.name} onChange={(value) => updateCommissionField('name', value)} />
                    <FormField label="Website" value={commissionForm?.website} onChange={(value) => updateCommissionField('website', value)} />
                    <SimpleSelectField label="Dependency" value={commissionForm?.dependency} options={metadata.dependencies} onChange={(value) => updateCommissionField('dependency', value)} />
                    <SimpleSelectField label="Legal form category" value={commissionForm?.legalFormCategory} options={metadata.legalForms} onChange={(value) => updateCommissionField('legalFormCategory', value)} />
                    <CodeSelectField label="CAP topic code" value={commissionForm?.capCode} options={metadata.capTopics} onChange={(code) => {
                      const selected = metadata.capTopics.find((item) => item.code === code);
                      updateCommissionField('capCode', code);
                      updateCommissionField('capTopic', selected?.label || '');
                    }} />
                    <CodeSelectField label="CAP subtopic code" value={commissionForm?.capSubcode} options={metadata.capSubtopics} onChange={(code) => {
                      const selected = metadata.capSubtopics.find((item) => item.code === code);
                      updateCommissionField('capSubcode', code);
                      updateCommissionField('capSubtopic', selected?.label || '');
                    }} />
                    <FormField label="Created year" value={commissionForm?.createdYear} onChange={(value) => updateCommissionField('createdYear', value)} />
                    <SimpleSelectField label="Legislature" value={commissionForm?.legislature} options={metadata.legislatures} onChange={(value) => updateCommissionField('legislature', value)} />
                    <FormField label="Created date" value={commissionForm?.createdDate} onChange={(value) => updateCommissionField('createdDate', value)} type="date" />
                    <FormField label="Latest norm date" value={commissionForm?.latestNormDate} onChange={(value) => updateCommissionField('latestNormDate', value)} type="date" />
                    <label className="block">
                      <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Repealed</span>
                      <select value={commissionForm?.repealed ? '1' : '0'} onChange={(e) => updateCommissionField('repealed', e.target.value === '1')} className="w-full border border-stone-300 p-2 text-sm bg-white">
                        <option value="0">No</option>
                        <option value="1">Yes</option>
                      </select>
                    </label>
                    <FormField label="Matching name" value={commissionForm?.matchingName} onChange={(value) => updateCommissionField('matchingName', value)} />
                    <FormField label="Matching ID" value={commissionForm?.matchingId} onChange={(value) => updateCommissionField('matchingId', value)} />
                    <FormField label="IDnous" value={commissionForm?.idnous} onChange={(value) => updateCommissionField('idnous', value)} />
                    <FormField label="Q-DEM corrected" value={commissionForm?.qDemCorretgit} onChange={(value) => updateCommissionField('qDemCorretgit', value)} />
                    <FormField label="Pepe Mal" value={commissionForm?.pepeMal} onChange={(value) => updateCommissionField('pepeMal', value)} />
                    <SimpleSelectField label="SPI type" value={commissionForm?.spiType} options={metadata.spiTypes} onChange={(value) => updateCommissionField('spiType', value)} />
                    <SimpleSelectField label="Economic endowment type" value={commissionForm?.economicEndowmentType} options={metadata.economicEndowmentTypes} onChange={(value) => updateCommissionField('economicEndowmentType', value)} />
                    <SimpleSelectField label="Members paid type" value={commissionForm?.membersPaidType} options={metadata.membersPaidTypes} onChange={(value) => updateCommissionField('membersPaidType', value)} />
                  </div>
                  <FormField label="Latest norm text" value={commissionForm?.latestNormText} onChange={(value) => updateCommissionField('latestNormText', value)} multiline />
                  <FormField label="Functions" value={commissionForm?.functions} onChange={(value) => updateCommissionField('functions', value)} multiline />
                  <FormField label="Composition" value={commissionForm?.composition} onChange={(value) => updateCommissionField('composition', value)} multiline />
                  <FormField label="Legal form text" value={commissionForm?.legalFormText} onChange={(value) => updateCommissionField('legalFormText', value)} multiline />
                  <FormField label="Economic endowment text" value={commissionForm?.economicEndowmentText} onChange={(value) => updateCommissionField('economicEndowmentText', value)} multiline />
                  <FormField label="Members paid text" value={commissionForm?.membersPaidText} onChange={(value) => updateCommissionField('membersPaidText', value)} multiline />
                </div>
                )}

                {editTab === 'members' && (
                <div className="bg-white rounded-sm shadow-sm border border-stone-200 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-stone-500">Members (editable)</h3>
                    <button onClick={addMemberRow} className="px-3 py-1.5 text-xs border border-stone-300 bg-white hover:bg-stone-50">Add row</button>
                  </div>
                  <div className="space-y-4 max-h-[980px] overflow-auto pr-1">
                    {membersForm.map((member, index) => (
                      <div key={`${member.id || 'new'}-${index}`} className="border border-stone-200 p-3 rounded-sm bg-stone-50 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Member #{index + 1}</div>
                          <button onClick={() => removeMemberRow(index)} className="text-xs text-red-700 hover:text-red-900">Remove</button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <FormField label="ID" value={member.id || ''} onChange={(value) => updateMemberField(index, 'id', value)} />
                          <FormField label="Representative" value={member.representativeName || ''} onChange={(value) => updateMemberField(index, 'representativeName', value)} />
                          <FormField label="Organization" value={member.organization || ''} onChange={(value) => updateMemberField(index, 'organization', value)} />
                          <FormField label="Acronym" value={member.acronym || ''} onChange={(value) => updateMemberField(index, 'acronym', value)} />
                          <FormField label="Source name" value={member.sourceName || ''} onChange={(value) => updateMemberField(index, 'sourceName', value)} />
                          <CodeSelectField label="CAP topic code" value={member.capCode} options={metadata.capTopics} onChange={(code) => {
                            const selected = metadata.capTopics.find((item) => item.code === code);
                            updateMemberField(index, 'capCode', code);
                            updateMemberField(index, 'capTopic', selected?.label || '');
                          }} />
                          <CodeSelectField label="CAP subtopic code" value={member.capSubcode} options={metadata.capSubtopics} onChange={(code) => {
                            const selected = metadata.capSubtopics.find((item) => item.code === code);
                            updateMemberField(index, 'capSubcode', code);
                            updateMemberField(index, 'capSubtopic', selected?.label || '');
                          }} />
                          <CodeSelectField label="Actor type code" value={member.actorTypeCode} options={metadata.actorTypes} onChange={(code) => {
                            const selected = metadata.actorTypes.find((item) => item.code === code);
                            updateMemberField(index, 'actorTypeCode', code);
                            updateMemberField(index, 'actorTypeLabel', selected?.label || '');
                          }} />
                          <CodeSelectField label="Interest group type" value={member.interestGroupType} options={metadata.interestGroupTypes} onChange={(code) => {
                            const selected = metadata.interestGroupTypes.find((item) => item.code === code);
                            updateMemberField(index, 'interestGroupType', code);
                            updateMemberField(index, 'interestGroupLabel', selected?.label || '');
                          }} />
                          <FormField label="Actor description" value={member.actorDescription || ''} onChange={(value) => updateMemberField(index, 'actorDescription', value)} />
                          <FormField label="Interest group type alt" value={member.interestGroupTypeAlt || ''} onChange={(value) => updateMemberField(index, 'interestGroupTypeAlt', value)} />
                          <FormField label="Interest group summary" value={member.interestGroupSummary || ''} onChange={(value) => updateMemberField(index, 'interestGroupSummary', value)} />
                          <FormField label="Primary ministry" value={member.ministryPrimary || ''} onChange={(value) => updateMemberField(index, 'ministryPrimary', value)} />
                          <FormField label="Secondary ministry" value={member.ministrySecondary || ''} onChange={(value) => updateMemberField(index, 'ministrySecondary', value)} />
                          <FormField label="Created date" value={member.createdDate || ''} onChange={(value) => updateMemberField(index, 'createdDate', value)} type="date" />
                          <FormField label="BOE" value={member.boe || ''} onChange={(value) => updateMemberField(index, 'boe', value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>
            )}

            {!isEditMode && (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 xl:col-span-7 bg-white rounded-sm shadow-sm border border-stone-200 p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Website</label>
                    {selectedCommission.website ? <a href={selectedCommission.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline flex items-center gap-1"><Globe size={14} /> {selectedCommission.website}</a> : <div className="text-stone-400">—</div>}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Legislature</label>
                    <div className="text-lg text-stone-800">{selectedCommission.legislature || '—'}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Created</label>
                    <div className="text-lg text-stone-800">{selectedCommission.createdYear || '—'}{selectedCommission.createdDate ? ` · ${formatIsoDate(selectedCommission.createdDate)}` : ''}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Latest Norm Date</label>
                    <div className="text-lg text-stone-800">{formatIsoDate(selectedCommission.latestNormDate)}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Dependency</label>
                    <div className="text-lg text-stone-800">{selectedCommission.dependency || '—'}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Legal Form</label>
                    <div className="text-lg text-stone-800">{selectedCommission.legalFormCategory || '—'}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Latest Norm</label>
                  <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">{selectedCommission.latestNormText || <span className="text-stone-400 italic">No information available.</span>}</div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Functions</label>
                  <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">{selectedCommission.functions || <span className="text-stone-400 italic">No information available.</span>}</div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Composition</label>
                  <div className="p-4 bg-stone-50 border border-stone-100 rounded-sm text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">{selectedCommission.composition || <span className="text-stone-400 italic">No information available.</span>}</div>
                </div>
              </div>

              <div className="col-span-12 xl:col-span-5 space-y-6">
                <div className="bg-white rounded-sm shadow-sm border border-stone-200 p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-stone-500 mb-4">Additional variables</h3>
                  <div className="space-y-4 text-sm text-stone-700">
                    <div>
                      <div className="text-xs font-bold uppercase text-stone-400 mb-1">Legal Form Copy</div>
                      <div className="whitespace-pre-wrap">{selectedCommission.legalFormText || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-stone-400 mb-1">Economic Endowment</div>
                      <div className="whitespace-pre-wrap">{selectedCommission.economicEndowmentText || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-stone-400 mb-1">Members Paid</div>
                      <div className="whitespace-pre-wrap">{selectedCommission.membersPaidText || '—'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-sm shadow-sm border border-stone-200 p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-stone-500 mb-4">Members</h3>
                  <div className="text-sm text-stone-500 mb-3">{selectedCommission.memberCount} rows · {selectedCommission.namedMemberCount} named · {selectedCommission.unnamedMemberCount} unnamed</div>
                  <div className="max-h-[520px] overflow-auto border border-stone-100">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-stone-100 text-xs uppercase text-stone-600">
                        <tr>
                          <th className="p-2">Representative</th>
                          <th className="p-2">Organisation</th>
                          <th className="p-2">Actor Type</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {selectedCommission.members.map((member) => (
                          <tr key={member.id || `${member.actorDescription}-${member.organization}`} className="align-top">
                            <td className="p-2">{member.representativeName || member.actorDescription || '—'}</td>
                            <td className="p-2">{member.organization || member.ministryPrimary || member.acronym || '—'}</td>
                            <td className="p-2">{member.actorTypeLabel || member.interestGroupLabel || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </div>
            )}
          </div>
        )}

        {view === 'data' && user?.role !== 'viewer' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
              <h2 className="text-3xl font-bold text-stone-900">Data Management</h2>
              <button onClick={fetchDashboard} className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50">
                <RefreshCw size={14} /> Refresh status
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {user?.role === 'admin' && (
              <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-green-100 text-green-800 rounded-full">
                    <Upload size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-800">Import XLSX</h3>
                    <p className="text-sm text-stone-500">Upload and replace the active Spanish Commissions workbook.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:bg-stone-50 transition-colors relative">
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setImportMsg(null);
                        setLoading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const res = await authFetchLocal(`${apiUrl}/spanish-commissions/import-xlsx`, {
                            method: 'POST',
                            body: formData
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data?.error || 'Import failed');
                          setImportMsg({ type: 'success', text: `Imported workbook: ${data?.summary?.totalCommissions || 0} commissions, ${data?.summary?.totalMembers || 0} member rows.` });
                          await fetchDashboard();
                          if (view === 'commissions') await fetchRows();
                        } catch (error) {
                          setImportMsg({ type: 'error', text: error.message || 'Import failed' });
                        } finally {
                          setLoading(false);
                          e.target.value = '';
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="text-stone-400 flex flex-col items-center">
                      {loading ? <RefreshCw className="animate-spin mb-2" /> : <Upload className="mb-2" size={32} />}
                      <span className="font-medium text-stone-600">Click to upload XLSX file</span>
                      <span className="text-xs mt-1">Expected source: merged by ID_qdem from the Q-DEM workbook</span>
                    </div>
                  </div>

                  {importMsg && (
                    <div className={`p-3 rounded-sm text-sm ${importMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                      {importMsg.text}
                    </div>
                  )}
                </div>
              </div>
              )}

              <div className={`bg-white p-8 rounded-sm shadow-sm border border-stone-200 ${user?.role === 'admin' ? '' : 'lg:col-span-2'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-stone-100 text-stone-800 rounded-full">
                    <Database size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-800">Export / Source</h3>
                    <p className="text-sm text-stone-500">Download data in CSV, Excel or original workbook format.</p>
                  </div>
                </div>

                <div className="space-y-4 text-sm text-stone-700">
                  <button onClick={handleExportCommissionsCsv} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="text-green-600" size={20} />
                      <div className="text-left">
                        <div className="font-bold text-stone-800 group-hover:text-green-800">Export full CSV</div>
                        <div className="text-xs text-stone-500">All commissions (normalized rows)</div>
                      </div>
                    </div>
                    <Download size={18} />
                  </button>

                  <button onClick={handleExportCommissionsXlsx} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="text-green-600" size={20} />
                      <div className="text-left">
                        <div className="font-bold text-stone-800 group-hover:text-green-800">Export full Excel (.xlsx)</div>
                        <div className="text-xs text-stone-500">Workbook with commissions and members sheets</div>
                      </div>
                    </div>
                    <Download size={18} />
                  </button>

                  <button onClick={handleExportCommissionsOriginal} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                    <div className="flex items-center gap-3">
                      <HardDrive className="text-green-600" size={20} />
                      <div className="text-left">
                        <div className="font-bold text-stone-800 group-hover:text-green-800">Download full DB in original format</div>
                        <div className="text-xs text-stone-500">Current storage engine snapshot (.db SQLite / .sql PostgreSQL)</div>
                      </div>
                    </div>
                    <Download size={18} />
                  </button>

                  <div>
                    <div className="text-xs font-bold uppercase text-stone-400 mb-1">Storage engine</div>
                    <div className="capitalize">{dashboard?.storageEngine || 'sqlite'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase text-stone-400 mb-1">Loaded sheets</div>
                    <div className="flex flex-wrap gap-2">{(dashboard?.sheets || []).map((sheet) => <span key={sheet} className="px-2 py-1 bg-stone-100 rounded-full text-xs">{sheet}</span>)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? <div className="fixed bottom-4 right-4 bg-stone-800 text-white text-xs px-3 py-2 rounded-sm shadow-lg">Loading...</div> : null}
      </main>
    </div>
  );
}
